import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSupabaseClient } from '../supabaseClient';
import { useBusinessType } from '../context/BusinessContext';
import { 
  Building2, 
  Loader, 
  ExternalLink, 
  AlertCircle,
  Check, 
  Info, 
  X
} from 'lucide-react';
import { toast } from '../components/ui/sonner-toast';
import { motion, AnimatePresence } from 'framer-motion';
import PageHeader from '../components/PageHeader';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';

const CurbsideIntegration = () => {
  const { businessType } = useBusinessType();
  const supabase = getSupabaseClient(businessType);
  const navigate = useNavigate();
  
  // State for form inputs
  const [url, setUrl] = useState('');
  const [signupUrl, setSignupUrl] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  
  // State for loading, errors, and settings ID
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [curbsideSettingsId, setCurbsideSettingsId] = useState(null);
  const [showSetupInstructions, setShowSetupInstructions] = useState(false);
  const [userData, setUserData] = useState(null);
  const [integrationStatus, setIntegrationStatus] = useState('not_started');
  
  // Add validation state
  const [formIsValid, setFormIsValid] = useState(false);

  // Animation variants
  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1],
        staggerChildren: 0.15
      }
    }
  };

  const cardVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.2,
        ease: [0, 0, 0.2, 1]
      }
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  // Handle input change - simplified without auto-save
  const handleInputChange = (field, value) => {
    // Update the specific field state
    switch (field) {
      case 'url':
        setUrl(value);
        break;
      case 'signupUrl':
        setSignupUrl(value);
        break;
      case 'username':
        setUsername(value);
        break;
      case 'password':
        setPassword(value);
        break;
      default:
        break;
    }
    
    // Check form validity on every change
    const isValid = 
      (field === 'url' ? value.trim() : url.trim()) !== '' && 
      (field === 'username' ? value.trim() : username.trim()) !== '' && 
      (field === 'password' ? value.trim() : password.trim()) !== '';
    
    setFormIsValid(isValid);
  };

  // Fetch existing settings
  const fetchSettings = async () => {
    try {
      setLoading(true);
      
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      
      if (user) {
        setUserData(user);
        
        // Check for existing settings
        const { data, error } = await supabase
          .from('curbside_settings')
          .select('*')
          .eq('account_id', user.id)
          .maybeSingle();
          
        if (error && error.code !== 'PGRST116') throw error;
        
        if (data) {
          // Set form fields with existing data
          setUrl(data.url || '');
          setSignupUrl(data.signup_url || '');
          setUsername(data.username || '');
          setPassword(data.password || '');
          setCurbsideSettingsId(data.id);
          setIntegrationStatus(data.integration_status || 'not_started');
        }
      }
    } catch (err) {
      console.error('Error fetching Curbside settings:', err);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  // Send setup request to API
  const handleSetupCurbside = async () => {
    try {
      setLoading(true);
      
      // Variable to track the current settings ID (from state or newly created)
      let currentSettingsId = curbsideSettingsId;
      
      // First, ensure a row exists in the curbside_settings table
      if (!currentSettingsId) {
        // Get current user data if we don't have it
        if (!userData) {
          const { data: { user }, error: userError } = await supabase.auth.getUser();
          if (userError) throw userError;
          setUserData(user);
        }
        
        // Create a new record with current form values (even if empty)
        const { data, error } = await supabase
          .from('curbside_settings')
          .insert({
            account_id: userData.id,
            url: url.trim() || '',
            signup_url: signupUrl.trim() || '',
            username: username.trim() || '',
            password: password.trim() || '',
            service_options: {},
            integration_status: 'not_started',
            updated_at: new Date().toISOString()
          })
          .select()
          .single();
          
        if (error) throw error;
        
        // Store the ID locally and also update the state
        currentSettingsId = data.id;
        setCurbsideSettingsId(data.id);
      } else {
        // Update the existing record with current form values
        const { error } = await supabase
          .from('curbside_settings')
          .update({
            url: url.trim() || '',
            signup_url: signupUrl.trim() || '',
            username: username.trim() || '',
            password: password.trim() || '',
            updated_at: new Date().toISOString()
          })
          .eq('id', currentSettingsId);
          
        if (error) throw error;
      }
      
      // Validate required fields now that we have a row
      if (!url.trim() || !username.trim() || !password.trim()) {
        toast.error("Please fill out the required fields first");
        setLoading(false);
        return;
      }
      
      // Update the integration status to in_progress - using the local ID variable
      const { error: updateError } = await supabase
        .from('curbside_settings')
        .update({ integration_status: 'in_progress' })
        .eq('id', currentSettingsId);
        
      if (updateError) throw updateError;
      
      // Update local state
      setIntegrationStatus('in_progress');
      
      // Make API request to setup Curbside
      try {
        const response = await fetch('https://cleaner-automation-936dbbf97f9e.herokuapp.com/api/browser/curbside/setup', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            accountId: userData.id
          })
        });
        
        if (!response.ok) {
          console.error('API setup request failed but continuing with refresh');
        } else {
          toast.success("Curbside integration setup started successfully");
        }
      } catch (apiError) {
        console.error('API request error:', apiError);
        toast.error("Setup initiated but API request failed. The page will refresh.");
      }
      
      // Always refresh the page after a brief delay, regardless of API success
      setTimeout(() => {
        window.location.reload();
      }, 1000);
      
    } catch (err) {
      console.error('Error setting up Curbside:', err);
      setError(err.message || 'Failed to setup Curbside integration');
      toast.error(err.message || 'Failed to setup Curbside integration');
      
      // Still refresh after error (after user sees the error message)
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !userData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="space-y-4 text-center">
          <Loader className="w-8 h-8 text-gray-500 animate-spin mx-auto" />
          <p className="text-sm text-gray-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div 
      className="min-h-screen bg-gray-50 flex flex-col items-center"
      variants={pageVariants}
      initial="initial"
      animate="animate"
    >
      <div className="w-full max-w-4xl px-4">
        <PageHeader
          title="Curbside Integration"
          description="Connect to Curbside POS for laundry services integration"
          icon={Building2}
        />

        <main className="w-full mx-auto py-8 space-y-6">
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="mb-6 p-4 bg-red-50 border border-red-100 rounded-lg flex items-center text-red-600 text-sm"
              >
                <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                {error}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Only render the content when not loading */}
          {!loading ? (
            <>
              {/* Integration Status Messages */}
              <motion.div variants={cardVariants} className="mb-6">
                {integrationStatus === 'complete' && (
                  <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
                    <div className="flex items-center">
                      <div className="bg-green-50 p-2 rounded-full mr-4 flex-shrink-0">
                        <Check className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900 mb-1">Connected</h3>
                        <p className="text-sm text-gray-600">
                          Your Curbside integration is active. All features are ready to use.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {integrationStatus === 'in_progress' && (
                  <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
                    <div className="flex items-center">
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ 
                          duration: 2,
                          repeat: Infinity,
                          ease: "linear"
                        }}
                        className="mr-4 flex-shrink-0 bg-blue-50 p-2 rounded-full"
                      >
                        <Loader className="w-5 h-5 text-blue-500" />
                      </motion.div>
                      <div>
                        <h3 className="font-medium text-gray-900 mb-1">Connecting</h3>
                        <p className="text-sm text-gray-600">
                          Setting up your integration. We'll notify you when it's ready.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {integrationStatus === 'not_started' && (
                  <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
                    <div className="flex items-start">
                      <div className="bg-blue-50 p-2 rounded-full mr-4 flex-shrink-0 mt-0.5">
                        <Info className="w-5 h-5 text-blue-500" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900 mb-1">Setup Required</h3>
                        <p className="text-sm text-gray-600 mb-3">
                          Create an employee account on Curbside POS before connecting.
                        </p>
                        <Button 
                          variant="outline"
                          className="text-blue-600 border-blue-200 hover:bg-blue-100 text-sm px-4 h-9"
                          onClick={() => setShowSetupInstructions(true)}
                        >
                          View Instructions
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>

              {/* Settings Form */}
              <motion.div variants={cardVariants} className="bg-white rounded-xl border border-gray-200 shadow-lg p-8">
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="url" className="text-gray-700">
                      Curbside Subdomain <span className="text-red-500">*</span>
                    </Label>
                    <div className="mt-1 relative">
                      <Input
                        id="url"
                        placeholder="subdomain"
                        value={url}
                        onChange={(e) => handleInputChange('url', e.target.value)}
                        className="pr-[180px]"
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center pointer-events-none pr-3">
                        <span className="text-gray-500 text-sm">-pds.curbsidelaundries.com</span>
                      </div>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Enter only the subdomain part of your Curbside URL
                    </p>
                  </div>
                  
                  <div>
                    <Label htmlFor="signup_url" className="text-gray-700">
                      Customer Signup URL
                    </Label>
                    <Input
                      id="signup_url"
                      placeholder="https://example.com/signup"
                      value={signupUrl}
                      onChange={(e) => handleInputChange('signupUrl', e.target.value)}
                      className="mt-1"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      The URL to send customers when they want to create a new account
                    </p>
                  </div>
                  
                  <div>
                    <Label htmlFor="username" className="text-gray-700">
                      Username <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="username"
                      placeholder="bella@aiforlaundromats.com"
                      value={username}
                      onChange={(e) => handleInputChange('username', e.target.value)}
                      className="mt-1"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Email used for the employee account you created
                    </p>
                  </div>
                  
                  <div>
                    <Label htmlFor="password" className="text-gray-700">
                      Password <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      className="mt-1"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Password for the employee account you created
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Setup Integration Button */}
              <motion.div variants={cardVariants} className="mt-6">
                {integrationStatus === 'not_started' && (
                  <>
                    <Button
                      onClick={handleSetupCurbside}
                      className="w-full bg-gray-900 hover:bg-gray-800 text-white py-3 h-auto text-base font-normal"
                      disabled={loading || !formIsValid}
                    >
                      <ExternalLink className="w-5 h-5 mr-2" />
                      Connect Integration
                    </Button>
                    {!formIsValid && (
                      <p className="text-center text-gray-500 text-sm mt-2">
                        Complete all required fields to continue
                      </p>
                    )}
                  </>
                )}
              </motion.div>
            </>
          ) : (
            <div className="h-[400px]"></div> /* Empty space as placeholder while loading */
          )}
        </main>
      </div>
      
      {/* Setup Instructions Modal - Refined for minimal elegance */}
      {showSetupInstructions && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[10001] overflow-y-auto backdrop-blur-sm">
          <div className="bg-white rounded-md w-full max-w-[700px] mx-auto my-8 shadow-sm border border-gray-200">
            <div className="p-6 pb-4 flex items-start justify-between">
              <div>
                <h2 className="text-lg font-medium text-gray-900">Curbside Setup Instructions</h2>
                <p className="text-gray-500 text-sm mt-1">Follow these steps to connect your account.</p>
              </div>
              <button
                onClick={() => setShowSetupInstructions(false)}
                className="text-gray-400 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="px-6 pb-6 overflow-y-auto">
              <div className="space-y-6">
                <div className="flex">
                  <div className="flex-shrink-0 mr-4 mt-0.5">
                    <div className="w-6 h-6 bg-black rounded-full text-white flex items-center justify-center text-sm font-medium">1</div>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Access Admin Interface</h3>
                    <div className="bg-gray-50 rounded mt-2 p-2">
                      <code className="text-sm">https://<span className="text-blue-600">your-subdomain</span>-pds.curbsidelaundries.com/User</code>
                    </div>
                  </div>
                </div>
                
                <div className="flex">
                  <div className="flex-shrink-0 mr-4 mt-0.5">
                    <div className="w-6 h-6 bg-black rounded-full text-white flex items-center justify-center text-sm font-medium">2</div>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Create Employee Account</h3>
                    <p className="text-sm text-gray-600 mt-1 mb-2">Click + New Employee and enter:</p>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex">
                        <div className="w-24 text-gray-500">Name</div>
                        <div>Any name</div>
                      </div>
                      <div className="flex">
                        <div className="w-24 text-gray-500">Email</div>
                        <div>bella@aiforlaundromats.com</div>
                      </div>
                      <div className="flex">
                        <div className="w-24 text-gray-500">Password</div>
                        <div>Create a secure password</div>
                      </div>
                      <div className="flex">
                        <div className="w-24 text-gray-500">Phone</div>
                        <div>Your business number</div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex">
                  <div className="flex-shrink-0 mr-4 mt-0.5">
                    <div className="w-6 h-6 bg-black rounded-full text-white flex items-center justify-center text-sm font-medium">3</div>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Set Permissions</h3>
                    <div className="space-y-2 text-sm mt-2">
                      <div className="flex">
                        <div className="text-gray-500 w-32">Current Role:</div>
                        <div>Manager</div>
                      </div>
                      <div className="flex">
                        <div className="text-gray-500 w-32">Switchable Roles:</div>
                        <div>All options</div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex">
                  <div className="flex-shrink-0 mr-4 mt-0.5">
                    <div className="w-6 h-6 bg-black rounded-full text-white flex items-center justify-center text-sm font-medium">4</div>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Complete Setup</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Save the employee account and enter your credentials on this page.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-4 border-t border-gray-100">
              <Button
                onClick={() => setShowSetupInstructions(false)}
                className="bg-gray-900 hover:bg-gray-800 text-white w-full"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default CurbsideIntegration; 